import { createSelector } from 'reselect';
import reducerRegistry from '@comeon/reducer-registry';
import { basicApiErrorHandler } from './we-spin-error-handler.js';
import { createApiAction } from '~/services/middleware/api-middleware.js';
import { ENDED, UPCOMING_STATES } from '../constants/we-spin-stream-states.js';

export const WE_SPIN_BASE_URL = '/we-spin-api';
const ACTION_PREFIX = '@komigen/we-spin';
const REDUCER_NAME = 'WeSpinStreamer';

// Events
const ADD_FOLLOWED_STREAMER = `${ACTION_PREFIX}/ADD_FOLLOWED_STREAMER`;
const REMOVE_FOLLOWED_STREAMER = `${ACTION_PREFIX}/REMOVE_FOLLOWED_STREAMER`;
const SET_FOLLOWED_STREAMERS_LIST = `${ACTION_PREFIX}/SET_FOLLOWED_STREAMERS_LIST`;
const SET_CURRENT_STREAMER_REF = `${ACTION_PREFIX}/CURRENT_STREAMER_REF`;
const SET_CURRENT_STREAMER_PROFILE = `${ACTION_PREFIX}/CURRENT_STREAMER_PROFILE`;
const SET_CURRENT_STREAMER_UPCOMING_EVENTS_LIST = `${ACTION_PREFIX}/SET_CURRENT_STREAMER_UPCOMING_EVENTS_LIST`;
const SET_CURRENT_STREAMER_ENDED_EVENTS_LIST = `${ACTION_PREFIX}/SET_CURRENT_STREAMER_ENDED_EVENTS_LIST`;

// Selectors
export const selectFollowedStreamersList = state => state[REDUCER_NAME].followedStreamersList;

export const selectCurrentStreamerRef = state => state[REDUCER_NAME].currentStreamerRef;

export const selectIsCurrentStreamerFollowed = createSelector(
	[selectCurrentStreamerRef, selectFollowedStreamersList],
	(streamerRef, followedStreamersList) => followedStreamersList.indexOf(streamerRef) !== -1
);

export const selectCurrentStreamerProfile = state => state[REDUCER_NAME].currentStreamerProfile;

export const selectCurrentStreamerUpcomingEventsList = state => state[REDUCER_NAME].currentStreamerUpcomingEventsList;

export const selectCurrentStreamerEndedEventsList = state => state[REDUCER_NAME].currentStreamerEndedEventsList;

// Action Creators

export const addFollowedStreamer = streamerRef => ({
	type: ADD_FOLLOWED_STREAMER,
	payload: streamerRef
});

export const removeFollowedStreamer = streamerRef => ({
	type: REMOVE_FOLLOWED_STREAMER,
	payload: streamerRef
});

export const setFollowedStreamersList = followedStreamersList => ({
	type: SET_FOLLOWED_STREAMERS_LIST,
	payload: followedStreamersList
});

export const setCurrentStreamerRef = currentStreamerRef => ({
	type: SET_CURRENT_STREAMER_REF,
	payload: currentStreamerRef
});

export const setCurrentStreamerProfile = currentStreamerProfile => ({
	type: SET_CURRENT_STREAMER_PROFILE,
	payload: currentStreamerProfile
});

export const setCurrentStreamerUpcomingEventsList = currentStreamerUpcomingEventsList => ({
	type: SET_CURRENT_STREAMER_UPCOMING_EVENTS_LIST,
	payload: currentStreamerUpcomingEventsList
});

export const setCurrentStreamerEndedEventsList = currentStreamerEndedEventsList => ({
	type: SET_CURRENT_STREAMER_ENDED_EVENTS_LIST,
	payload: currentStreamerEndedEventsList
});

export const fetchCurrentStreamerProfile = () => (dispatch, getState) => {
	const streamerRef = selectCurrentStreamerRef(getState());
	return dispatch(
		createApiAction({
			endpoint: `${WE_SPIN_BASE_URL}/view/streamer?streamerRef=${streamerRef}`,
			onSuccess: response => () => {
				dispatch(setCurrentStreamerProfile(response));
			},
			onError: basicApiErrorHandler
		})
	);
};

export const followStreamer = streamerRef =>
	createApiAction({
		endpoint: `${WE_SPIN_BASE_URL}/player/followStreamer`,
		method: 'POST',
		payload: {
			streamerRef
		},
		onError: basicApiErrorHandler,
		onSuccess: () => dispatch => {
			dispatch(addFollowedStreamer(streamerRef));
			dispatch(fetchCurrentStreamerProfile(streamerRef));
		}
	});

export const unfollowStreamer = streamerRef =>
	createApiAction({
		endpoint: `${WE_SPIN_BASE_URL}/player/unfollowStreamer`,
		method: 'POST',
		payload: {
			streamerRef
		},
		onError: basicApiErrorHandler,
		onSuccess: () => dispatch => {
			dispatch(removeFollowedStreamer(streamerRef));
			dispatch(fetchCurrentStreamerProfile(streamerRef));
		}
	});

export const fetchFollowedStreamersList = () =>
	createApiAction({
		endpoint: `${WE_SPIN_BASE_URL}/player/listFollowedStreamers`,
		onSuccess: response => dispatch =>
			dispatch(setFollowedStreamersList(response.map(streamer => streamer.streamerRef))),
		onError: basicApiErrorHandler
	});

export const fetchCurrentStreamerUpcomingEventsList = () => (dispatch, getState) => {
	const streamerRef = selectCurrentStreamerRef(getState());
	const endpoint = `${WE_SPIN_BASE_URL}/view/events?streamerRef=${streamerRef}&states=${UPCOMING_STATES.join(',')}`;
	return (
		!!streamerRef &&
		dispatch(
			createApiAction({
				endpoint,
				onSuccess: response => () => {
					dispatch(setCurrentStreamerUpcomingEventsList(response));
				},
				onError: basicApiErrorHandler
			})
		)
	);
};

export const fetchCurrentStreamerEndedEventsList = () => (dispatch, getState) => {
	const streamerRef = selectCurrentStreamerRef(getState());
	const endpoint = `${WE_SPIN_BASE_URL}/view/events?streamerRef=${streamerRef}&states=${ENDED}`;
	return (
		!!streamerRef &&
		dispatch(
			createApiAction({
				endpoint,
				onSuccess: response => () => {
					dispatch(setCurrentStreamerEndedEventsList(response));
				},
				onError: basicApiErrorHandler
			})
		)
	);
};

export const subscribeToStreamerEventsPolling = (dispatch, pollingInterval) => {
	const id = setInterval(() => {
		dispatch(fetchCurrentStreamerUpcomingEventsList());
		dispatch(fetchCurrentStreamerEndedEventsList());
	}, pollingInterval);

	return () => clearInterval(id);
};

const INIT_STATE = {
	followedStreamersList: [],
	currentStreamerRef: null,
	currentStreamerProfile: null,
	currentStreamerUpcomingEventsList: [],
	currentStreamerEndedEventsList: []
};

const reducer = (state = INIT_STATE, { type, payload } = { type: null, payload: null }) => {
	switch (type) {
		case ADD_FOLLOWED_STREAMER:
			return {
				...state,
				followedStreamersList: [...state.followedStreamersList, payload]
			};
		case REMOVE_FOLLOWED_STREAMER:
			return {
				...state,
				followedStreamersList: [...state.followedStreamersList].filter(streamerRef => streamerRef !== payload)
			};
		case SET_FOLLOWED_STREAMERS_LIST:
			return {
				...state,
				followedStreamersList: payload
			};
		case SET_CURRENT_STREAMER_REF:
			return {
				...state,
				currentStreamerRef: payload
			};
		case SET_CURRENT_STREAMER_PROFILE:
			return {
				...state,
				currentStreamerProfile: payload
			};
		case SET_CURRENT_STREAMER_UPCOMING_EVENTS_LIST:
			return {
				...state,
				currentStreamerUpcomingEventsList: payload
			};
		case SET_CURRENT_STREAMER_ENDED_EVENTS_LIST:
			return {
				...state,
				currentStreamerEndedEventsList: payload
			};
		default:
			return state;
	}
};

reducerRegistry.register(REDUCER_NAME, reducer);

export default reducer;
